import ReactSelect from "react-select";
import { Button } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { ArrowClockwise } from "@phosphor-icons/react";
import { customSelectStyles } from "../../../shared/constants";
import DateRangePickerButton from "../../../components/DateRangePicker/DateRangePickerButton";
import { useAuth } from "../../../hooks";
import { useTheme } from "../../../providers/ThemeProvider";

export interface UserActivityFilterValues {
  action_type?: string;
  action_group?: string;
  user_id?: string;
  from_date?: string;
  to_date?: string;
}

interface FilterProps {
  values: UserActivityFilterValues;
  onChange: (values: UserActivityFilterValues) => void;
  actionTypes: { value: string; label: string }[];
  userOptions?: { value: string; label: string }[];
  isLoading?: boolean;
  refetch: () => void;
  isRefetching: boolean;
  allowTodaySelection?: boolean;
}

// Helper to group action types using stable IDs (not translated labels)
function getGroupedActionTypes(
  actionTypes: { value: string; label: string }[],
  t: TFunction
): { id: string; label: string; options: { value: string; label: string }[] }[] {
  // Remove the 'All Activities' option
  const filtered = actionTypes.filter((a) => a.value !== "");
  // Grouping logic (customize as needed)
  const groups = [
    {
      id: "authentication",
      label: t("userActivity.actionTypeGroup.authentication", "Authentication"),
      options: filtered.filter((a) =>
        ["LOGIN", "LOGOUT", "SIGN_UP"].includes(a.value)
      ),
    },
    {
      id: "profile",
      label: t(
        "userActivity.actionTypeGroup.profile",
        "Profile & User Management"
      ),
      options: filtered.filter((a) =>
        [
          "PROFILE_UPDATE",
          "USER_ROLE_CHANGE",
          "USER_DELETE",
          "USER_DEACTIVATED",
          "USER_ACTIVATED",
        ].includes(a.value)
      ),
    },
    {
      id: "invite",
      label: t("userActivity.actionTypeGroup.invite", "Invitations"),
      options: filtered.filter((a) =>
        [
          "INVITE_USER_ACCEPT",
          "INVITE_USER_EMAIL_SENT",
          "INVITE_USER_DELETE",
          "INVITE_USER_UPDATE",
          "RESEND_INVITE",
        ].includes(a.value)
      ),
    },
    {
      id: "password",
      label: t("userActivity.actionTypeGroup.password", "Password Actions"),
      options: filtered.filter((a) =>
        [
          "PASSWORD_RESET_REQUEST",
          "PASSWORD_RESET_COMPLETE",
          "PASSWORD_CHANGE",
        ].includes(a.value)
      ),
    },
    {
      id: "roles",
      label: t("userActivity.actionTypeGroup.roles", "Roles & Permissions"),
      options: filtered.filter((a) =>
        ["ROLE_CREATE", "ROLE_UPDATE", "ROLE_DELETE"].includes(a.value)
      ),
    },
    {
      id: "preferences",
      label: t("userActivity.actionTypeGroup.preferences", "Preferences"),
      options: filtered.filter((a) =>
        [
          "DASHBOARD_PREFERENCE_UPDATE",
          "APP_LIST_PREFERENCE_UPDATE",
          "APP_DETAIL_PREFERENCE_UPDATE",
          "APP_COUNTRY_REPORT_PREFERENCE_UPDATE",
        ].includes(a.value)
      ),
    },
    {
      id: "bidding",
      label: t("userActivity.actionTypeGroup.bidding", "Bidding"),
      options: filtered.filter((a) => ["BID_CHANGE"].includes(a.value)),
    },
    {
      id: "campaigns",
      label: t("userActivity.actionTypeGroup.campaigns", "Campaign Management"),
      options: filtered.filter((a) =>
        [
          "GOOGLE_ADS_BID_CHANGE",
          "GOOGLE_ADS_BUDGET_CHANGE",
          "GOOGLE_ADS_STATUS_CHANGE",
          "APPLE_ADS_BID_CHANGE",
          "APPLE_ADS_BUDGET_CHANGE",
          "APPLE_ADS_STATUS_CHANGE",
          "META_ADS_BID_CHANGE",
          "META_ADS_BUDGET_CHANGE",
          "META_ADS_STATUS_CHANGE",
        ].includes(a.value)
      ),
    },
    {
      id: "integration",
      label: t(
        "userActivity.actionTypeGroup.integration",
        "Integrations"
      ),
      options: filtered.filter((a) =>
        [
          "INTEGRATION_START",
          "INTEGRATION_COMPLETE",
          "ADDON_ACCOUNT_DELETE",
          "ONGOING_ACCOUNT_DELETE",
          "ADDON_ACCOUNT_INTEGRATION_CANCEL",
          "ADDON_ACCOUNT_INTEGRATION_COMPLETE",
          "ADDON_ACCOUNT_INTEGRATION_START",
          "ENTIRE_TRACKING_DELETE",
        ].includes(a.value)
      ),
    },
    {
      id: "billing",
      label: t("userActivity.actionTypeGroup.billing", "Billing & Subscriptions"),
      options: filtered.filter((a) =>
        [
          "TRIAL_STARTED",
          "TRIAL_ENDED",
          "SUBSCRIPTION_CREATED",
          "SUBSCRIPTION_RENEWED",
          "SUBSCRIPTION_UPGRADED",
          "SUBSCRIPTION_DOWNGRADED",
          "SUBSCRIPTION_CANCELLED",
          "SUBSCRIPTION_REACTIVATED",
          "PAYMENT_SUCCEEDED",
          "PAYMENT_FAILED",
          "REFUND_REQUESTED",
          "REFUND_PROCESSED",
        ].includes(a.value)
      ),
    },
  ];

  // Add any ungrouped types to a Misc group
  // const groupedValues = groups.flatMap((g) => g.options.map((o) => o.value));
  // const misc = filtered.filter((a) => !groupedValues.includes(a.value));
  // if (misc.length > 0) {
  //   groups.push({
  //     label: t("userActivity.actionTypeGroup.misc", "Other"),
  //     options: misc,
  //   });
  // }
  return groups.filter((g) => g.options.length > 0);
}

export default function Filter({
  values,
  onChange,
  actionTypes,
  userOptions = [],
  isLoading = false,
  refetch,
  isRefetching,
  allowTodaySelection = false,
}: FilterProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const { user } = useAuth();

  const groupedActionTypes = getGroupedActionTypes(actionTypes, t);

  const groupOptions = [
    {
      label: t("userActivity.actionTypeGroup.allGroups", "All Categories"),
      value: "",
    },
    ...groupedActionTypes.map((g) => ({ label: g.label, value: g.id })),
  ];

  const actionTypeOptionsForSelect = values.action_group
    ? (groupedActionTypes.find((g) => g.id === values.action_group)?.options || [])
    : [
      // { label: t("common.allActivities"), value: "" },
      ...actionTypes,
    ];

  // Convert string dates to Date objects for the picker
  const initialStartDate = values.from_date
    ? new Date(values.from_date)
    : undefined;
  const initialEndDate = values.to_date ? new Date(values.to_date) : undefined;

  return (
    <div className="flex flex-wrap gap-2 items-end mb-4">
      <Button
        size="sm"
        color="light"
        onClick={() => refetch()}
        disabled={isRefetching}
      >
        <div className="flex items-center gap-1">
          {t("common.refresh")}
          <ArrowClockwise
            size={16}
            weight="duotone"
            className={`${isRefetching ? "animate-spin" : ""}`}
          />
        </div>
      </Button>
      <div className="min-w-[180px]">
        <ReactSelect
          isClearable={values.action_group !== ""}
          isLoading={isLoading}
          placeholder={t("userActivity.actionGroup") as string}
          classNamePrefix="custom-select"
          styles={customSelectStyles<{ value: string; label: string }>(isDark)}
          options={groupOptions}
          value={groupOptions.find((o) => o.value === values.action_group) || null}
          onChange={(option) => {
            const selectedGroup = (option as { value: string })?.value || "";
            // When a group is selected, reset action_type to '' (All Activities)
            onChange({
              ...values,
              action_group: selectedGroup,
              action_type: "",
            });
          }}
        />
      </div>
      <div className="min-w-[180px]">
        <ReactSelect
          isClearable={values.action_type !== ""}
          isLoading={isLoading}
          placeholder={t("userActivity.actionTypeLabel", "Activity") as string}
          classNamePrefix="custom-select"
          styles={customSelectStyles<{ value: string; label: string }>(isDark)}
          options={actionTypeOptionsForSelect}
          value={
            actionTypeOptionsForSelect.find(
              (o) => o.value === values.action_type
            ) || null
          }
          onChange={(option) =>
            onChange({
              ...values,
              action_type: (option as { value: string } | null)?.value || "",
            })
          }
        />
      </div>
      {!user?.is_owner && userOptions.length > 1 && (
        <div className="min-w-[180px]">
          <ReactSelect
            isClearable={values.user_id !== ""}
            isLoading={isLoading}
            placeholder={t("userActivity.user") as string}
            classNamePrefix="custom-select"
            styles={customSelectStyles<{ value: string; label: string }>(isDark)}
            options={userOptions}
            value={userOptions.find((o) => o.value === values.user_id) || null}
            onChange={(option) =>
              onChange({
                ...values,
                user_id: (option as { value: string })?.value || "",
              })
            }
          />
        </div>
      )}
      <div>
        <DateRangePickerButton
          onDateRangeChange={(start, end) => {
            onChange({
              ...values,
              from_date: start ? start.toISOString().slice(0, 10) : undefined,
              to_date: end ? end.toISOString().slice(0, 10) : undefined,
            });
          }}
          initialStartDate={initialStartDate}
          initialEndDate={initialEndDate}
          onPresetChange={() => { }}
          initialPreset="last7Days"
          allowTodaySelection={allowTodaySelection}
        />
      </div>
    </div>
  );
}
